import {
  AuthApiError,
  AuthSession,
  CreatePasswordRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  SignInRequest,
} from "../model/auth.types";

const wait = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

const createMockSession = (email: string): AuthSession => ({
  accessToken: `mock-access-token.${Date.now()}`,
  refreshToken: `mock-refresh-token.${Date.now()}`,
  user: {
    id: "mock-user-1",
    email,
    name: "Legacy Link User",
  },
});

const authError = (error: AuthApiError) => error;

export const authApi = {
  async signIn(request: SignInRequest): Promise<AuthSession> {
    await wait();

    const email = request.email.trim().toLowerCase();

    if (email === "expired@legacy-link.com") {
      throw authError({
        code: "PASSWORD_EXPIRED",
        message: "Your password has expired and can no longer be used.",
        createPasswordToken: "expired-password-token",
      });
    }

    if (email === "locked@legacy-link.com") {
      throw authError({
        code: "ACCOUNT_LOCKED",
        message: "This account is locked. Contact support for help.",
      });
    }

    if (
      email === "clinician@legacy-link.com" &&
      request.password === "Password1!"
    ) {
      return createMockSession(email);
    }

    throw authError({
      code: "INVALID_CREDENTIALS",
      message: "Incorrect email and/or password. Please check and try again.",
    });
  },

  async signInDemo(): Promise<AuthSession> {
    await wait();
    return createMockSession("demo@legacy-link.com");
  },

  async createPassword(request: CreatePasswordRequest): Promise<AuthSession> {
    await wait();

    if (!request.token || request.token === "invalid-create-token") {
      throw authError({
        code: "TOKEN_INVALID",
        message: "This password setup link is invalid.",
      });
    }

    if (request.token === "expired-create-token") {
      throw authError({
        code: "TOKEN_EXPIRED",
        message: "This password setup link has expired.",
      });
    }

    if (request.password.toLowerCase().includes("legacy")) {
      throw authError({
        code: "PASSWORD_POLICY_FAILED",
        message: "Password cannot include the product name.",
      });
    }

    return createMockSession("clinician@legacy-link.com");
  },

  async forgotPassword(
    request: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> {
    await wait();

    const email = request.email.trim().toLowerCase();

    if (!email) {
      throw authError({
        code: "INVALID_CREDENTIALS",
        message: "Enter the email address associated with your account.",
      });
    }

    if (email === "locked@legacy-link.com") {
      throw authError({
        code: "ACCOUNT_LOCKED",
        message: "This account is locked. Contact support for help.",
      });
    }

    const knownEmails = [
      "clinician@legacy-link.com",
      "expired@legacy-link.com",
      "locked@legacy-link.com",
    ];

    if (!knownEmails.includes(email)) {
      throw authError({
        code: "USER_NOT_FOUND",
        message: "User with this email was not found.",
      });
    }

    return {
      email,
      resetToken:
        email === "clinician@legacy-link.com"
          ? "valid-create-token"
          : undefined,
    };
  },

  async refreshSession(refreshToken: string): Promise<AuthSession> {
    await wait(150);

    if (!refreshToken.startsWith("mock-refresh-token")) {
      throw authError({
        code: "TOKEN_INVALID",
        message: "Session has expired. Please sign in again.",
      });
    }

    return createMockSession("clinician@legacy-link.com");
  },

  async logout(): Promise<void> {
    await wait(100);
  },
};
